import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
	useCreateUnlayerDraftIdMutation,
	useCreateUnlayerEmailDraftMutation,
	useDeleteUnlayerEmailDraftMutation,
	useLazyGetUnlayerEmailDraftDetailQuery,
	useLazyGetUnlayerEmailDraftSummariesQuery,
	useUpdateUnlayerEmailDraftMutation,
} from '@/features/unlayerEmailTemplates/unlayerEmailTemplatesApi';
import { showToast } from '@/features/toast/toastSlice';
import { getDesignFingerprint } from '@/components/Drawers/AddUnlayerEmailDrawer/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useFormikContext } from 'formik';
import { convertFilesForUpload } from '@/features/msGraph/helper';
import { storage } from '@/utils/firebase';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { STORAGE_PURPOSES, clientStoragePath, dealStoragePath } from '@/utils/storagePaths';

const UnlayerDraftContext = createContext(null);

export const uploadEmailAttachments = async (files, userId, folderPath) => {
	if (!files?.length) return [];

	const existing = [];
	const toUpload = [];

	for (const file of files) {
		if (file instanceof File) {
			toUpload.push(file);
			continue;
		}

		// Already stored attachments (templates/drafts) — keep metadata, don't re-upload
		if (file?.downloadUrl || file?.url) {
			existing.push({
				fileName: file.name ?? file.fileName,
				fileSize: file.size ?? file.fileSize ?? 0,
				mimeType: file.mimeType ?? file.type ?? '',
				downloadUrl: file.downloadUrl ?? file.url,
				uploadedBy: file.uploadedBy ?? userId,
				storagePath: file.storagePath ?? '',
			});
			continue;
		}

		// SharePoint / other remote file shapes still need resolving
		toUpload.push(file);
	}

	if (!toUpload.length) {
		return existing;
	}

	const resolvedFiles = await convertFilesForUpload(toUpload);
	if (resolvedFiles.length < toUpload.length) {
		throw new Error('Failed to upload email attachments.');
	}

	const uploaded = await Promise.all(
		resolvedFiles.map(async ({ file }) => {
			const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
			const filePath = `${folderPath}/${safeFileName}`;
			const storageRef = ref(storage, filePath);
			const metadata = file.type ? { contentType: file.type } : undefined;
			await uploadBytes(storageRef, file, metadata);
			const downloadUrl = await getDownloadURL(storageRef);
			return {
				fileName: file.name,
				fileSize: file.size,
				mimeType: file.type,
				downloadUrl,
				uploadedBy: userId,
				storagePath: filePath,
			};
		}),
	);

	return [...existing, ...uploaded];
};

// A deal draft's attachments sit under the deal; a client draft's under the client.
const getDraftAttachmentFolderPath = (draftId, { clientId, dealId }) =>
	dealId
		? dealStoragePath(
				{ clientDocId: clientId, dealDocId: dealId },
				STORAGE_PURPOSES.emailDrafts,
				draftId,
			)
		: clientStoragePath(clientId, STORAGE_PURPOSES.emailDrafts, draftId);
/**
 * Owns draft-specific state and API calls for the compose drawer.
 * Drafts are scoped by the active client/deal context, so this provider tracks
 * which draft is selected and whether the current editor state has diverged.
 */
export const UnlayerDraftProvider = ({ children }) => {
	const dispatch = useDispatch();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const userBrokerGroupIds = userDetails?.brokerGroupArray ?? [];

	const { values } = useFormikContext();

	const { subject, editorDesign, files: selectedAttachments } = values;

	const selectedClientId = values.client?.clientId ?? null;
	const selectedDealId = values.deal?.dealId ?? null;

	const [selectedDraftId, setSelectedDraftId] = useState('');
	const [pendingDraftSelectionId, setPendingDraftSelectionId] = useState('');
	const [hasLoadedDraftSummaries, setHasLoadedDraftSummaries] = useState(false);
	const [draftActionError, setDraftActionError] = useState('');
	const [loadedDraftSnapshot, setLoadedDraftSnapshot] = useState(null);
	const [isDeleteDraftDialogOpen, setIsDeleteDraftDialogOpen] = useState(false);
	const [isDraftReplaceDialogOpen, setIsDraftReplaceDialogOpen] = useState(false);
	const [createUnlayerEmailDraft, { isLoading: isSavingDraft }] =
		useCreateUnlayerEmailDraftMutation();
	const [deleteUnlayerEmailDraft, { isLoading: isDeletingDraft }] =
		useDeleteUnlayerEmailDraftMutation();
	const [loadDraftSummaries, { data: draftSummaries = [], isFetching: isFetchingDrafts }] =
		useLazyGetUnlayerEmailDraftSummariesQuery();
	const [loadDraftDetail] = useLazyGetUnlayerEmailDraftDetailQuery();
	const [updateUnlayerEmailDraft] = useUpdateUnlayerEmailDraftMutation();
	const [createUnlayerDraftId] = useCreateUnlayerDraftIdMutation();
	const canUseDraftContext = Boolean(selectedClientId || selectedDealId);
	const currentDraftFingerprint = useMemo(
		() => getDesignFingerprint(editorDesign),
		[editorDesign],
	);
	const isDraftDirty = Boolean(
		selectedDraftId &&
		loadedDraftSnapshot &&
		(loadedDraftSnapshot.subject !== subject ||
			loadedDraftSnapshot.designFingerprint !== currentDraftFingerprint),
	);
	const isAnyDraftActionLoading = isSavingDraft || isDeletingDraft || isFetchingDrafts;

	const reloadDraftSummaries = useCallback(
		async ({ clientId = selectedClientId, dealId = selectedDealId } = {}) => {
			if (userBrokerGroupIds.length === 0) return [];
			if (!clientId && !dealId) return [];
			setHasLoadedDraftSummaries(true);
			const result = await loadDraftSummaries({
				clientId,
				dealId,
				brokerGroupIds: userBrokerGroupIds,
			});
			return result?.data ?? [];
		},
		[loadDraftSummaries, selectedClientId, selectedDealId, userBrokerGroupIds],
	);

	const handleDraftSelectorOpen = useCallback(() => {
		if (hasLoadedDraftSummaries || userBrokerGroupIds.length === 0) {
			return;
		}
		if (!selectedClientId && !selectedDealId) {
			return;
		}
		setHasLoadedDraftSummaries(true);
		loadDraftSummaries({
			clientId: selectedClientId,
			dealId: selectedDealId,
			brokerGroupIds: userBrokerGroupIds,
		});
	}, [
		hasLoadedDraftSummaries,
		loadDraftSummaries,
		selectedClientId,
		selectedDealId,
		userBrokerGroupIds,
	]);

	const clearDraftSelection = useCallback(() => {
		setSelectedDraftId('');
		setLoadedDraftSnapshot(null);
	}, []);

	const loadDraftIntoCompose = useCallback(
		async (draftId) => {
			if (!draftId) {
				clearDraftSelection();
				return null;
			}
			const detail = await loadDraftDetail({
				id: draftId,
				userId: userDetails?.userId,
				brokerGroupIds: userBrokerGroupIds,
			}).unwrap();
			setSelectedDraftId(draftId);
			setLoadedDraftSnapshot({
				id: draftId,
				subject: detail?.subject ?? '',
				designFingerprint: getDesignFingerprint(detail?.designJson ?? null),
				attachments: detail?.attachments ?? [],
			});
			return detail;
		},
		[clearDraftSelection, loadDraftDetail, userBrokerGroupIds, userDetails?.userId],
	);

	const createDraft = useCallback(async () => {
		if (!canUseDraftContext || subject.trim().length === 0) {
			return null;
		}

		try {
			setDraftActionError('');
			const unlayerDraftDocId = await createUnlayerDraftId().unwrap();
			const scopeType = selectedDealId ? 'deal' : 'client';
			const attachments = selectedAttachments || [];
			let uploadedAttachments = [];

			if (attachments.length > 0) {
				uploadedAttachments = await uploadEmailAttachments(
					attachments,
					userDetails.userId,
					getDraftAttachmentFolderPath(unlayerDraftDocId, {
						clientId: selectedClientId,
						dealId: selectedDealId,
					}),
				);
			}

			const createdDraft = await createUnlayerEmailDraft({
				id: unlayerDraftDocId,
				scopeType,
				clientId: selectedClientId,
				dealId: selectedDealId,
				subject,
				designJson: editorDesign ?? {},
				brokerGroupIds: userBrokerGroupIds,
				attachments: uploadedAttachments,
			}).unwrap();

			await reloadDraftSummaries();
			setSelectedDraftId(createdDraft?.id ?? '');
			setLoadedDraftSnapshot({
				id: createdDraft?.id ?? '',
				subject,
				designFingerprint: getDesignFingerprint(editorDesign),
				attachments: uploadedAttachments,
			});
			dispatch(
				showToast({
					message: 'Draft created.',
					autohide: true,
				}),
			);
			return createdDraft;
		} catch (error) {
			setDraftActionError(error?.data?.message ?? 'Unable to create draft.');
			return null;
		}
	}, [
		canUseDraftContext,
		createUnlayerDraftId,
		createUnlayerEmailDraft,
		updateUnlayerEmailDraft,
		dispatch,
		editorDesign,
		reloadDraftSummaries,
		selectedClientId,
		selectedDealId,
		subject,
		userBrokerGroupIds,
		userDetails?.userId,
		selectedAttachments,
	]);

	const deleteSelectedDraft = useCallback(async () => {
		if (!selectedDraftId) return false;
		try {
			setDraftActionError('');
			await deleteUnlayerEmailDraft({
				id: selectedDraftId,
				userId: userDetails?.userId,
				brokerGroupIds: userBrokerGroupIds,
			}).unwrap();
			setIsDeleteDraftDialogOpen(false);
			clearDraftSelection();
			await reloadDraftSummaries();
			dispatch(
				showToast({
					message: 'Draft deleted.',
					autohide: true,
				}),
			);
			return true;
		} catch (error) {
			setDraftActionError(error?.data?.message ?? 'Unable to delete draft.');
			return false;
		}
	}, [
		clearDraftSelection,
		deleteUnlayerEmailDraft,
		dispatch,
		reloadDraftSummaries,
		selectedDraftId,
		userBrokerGroupIds,
		userDetails?.userId,
	]);

	const value = useMemo(
		() => ({
			canUseDraftContext,
			clearDraftSelection,
			createDraft,
			deleteSelectedDraft,
			draftActionError,
			draftSummaries,
			handleDraftSelectorOpen,
			hasLoadedDraftSummaries,
			isAnyDraftActionLoading,
			isDeleteDraftDialogOpen,
			isDeletingDraft,
			isDraftDirty,
			isDraftReplaceDialogOpen,
			isFetchingDrafts,
			loadDraftIntoCompose,
			loadedDraftSnapshot,
			pendingDraftSelectionId,
			reloadDraftSummaries,
			selectedDraftId,
			setDraftActionError,
			setHasLoadedDraftSummaries,
			setIsDeleteDraftDialogOpen,
			setIsDraftReplaceDialogOpen,
			setLoadedDraftSnapshot,
			setPendingDraftSelectionId,
			setSelectedDraftId,
			loadedDraftAttachments: loadedDraftSnapshot?.attachments ?? [],
		}),
		[
			canUseDraftContext,
			clearDraftSelection,
			createDraft,
			deleteSelectedDraft,
			draftActionError,
			draftSummaries,
			handleDraftSelectorOpen,
			hasLoadedDraftSummaries,
			isAnyDraftActionLoading,
			isDeleteDraftDialogOpen,
			isDeletingDraft,
			isDraftDirty,
			isDraftReplaceDialogOpen,
			isFetchingDrafts,
			loadDraftIntoCompose,
			loadedDraftSnapshot,
			pendingDraftSelectionId,
			reloadDraftSummaries,
			selectedDraftId,
		],
	);

	return <UnlayerDraftContext.Provider value={value}>{children}</UnlayerDraftContext.Provider>;
};

export const useUnlayerDraftContext = () => {
	const context = useContext(UnlayerDraftContext);
	if (!context) {
		throw new Error('useUnlayerDraftContext must be used within an UnlayerDraftProvider');
	}
	return context;
};
